import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import {
  ArrowLeft,
  Settings2,
  RefreshCw,
  Maximize2,
  Minimize2,
  Share2,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  Rotate3d,
} from "lucide-react";
import { ViewerHost } from "../ViewerHost";
import Logo from "../../shared/brand/Logo";
import { Loader, Pill } from "@/shared/ui";
import { EASE } from "@/shared/motion/transitions";
import { useAuth } from "@/features/auth/context/AuthContext";
import type { ViewerClient } from "../../client/ViewerClient";
import {
  defaultIndexOf,
  effectiveConfigurator,
  formatPrice,
  matchGroupTargets,
  type ConfiguratorDef,
  type ConfiguratorOptionGroup,
} from "../../features/vault/configurator";
import { getModel, getModelGlbUrl, type VaultModel } from "../../features/vault/vaultApi";
import ShareModal from "./ShareModal";
import { capsFor } from "../../ui/editor/caps";
import { withEmbeddedScene } from "../../util/embeddedScene";
import EmbedSettings from "../../features/embed/EmbedSettings";

const fmtSize = (bytes?: number) => {
  if (!bytes) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) (v /= 1024), i++;
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

interface LiveGroup extends ConfiguratorOptionGroup {
  groupId: string;
  activeIndex: number;
}

/** Read-only viewer for an authed user: 3D model + info sidebar + controls +
 *  configurator swatches. Editing happens in Studio; sharing in the Share modal. */
export default function ModelViewPage() {
  const { modelId } = useParams<{ modelId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const stageRef = useRef<HTMLDivElement>(null);

  const [model, setModel] = useState<VaultModel | null>(null);
  const [glbReady, setGlbReady] = useState(false);
  const [groups, setGroups] = useState<LiveGroup[]>([]);
  const [meta, setMeta] = useState<{ basePrice?: number; currency: string }>({ currency: "USD" });
  const [sidebar, setSidebar] = useState(true);
  const [rotate, setRotate] = useState(false);
  const [fs, setFs] = useState(false);
  const [share, setShare] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);

  useEffect(() => {
    if (!modelId) return;
    getModel(modelId).then(setModel).catch(() => navigate("/vault"));
  }, [modelId, navigate]);

  useEffect(() => {
    const onFs = () => setFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void stageRef.current?.requestFullscreen?.();
  };

  const price = (meta.basePrice ?? 0) + groups.reduce((s, g) => s + (g.options[g.activeIndex]?.priceDelta ?? 0), 0);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-white text-ink-900">
      {/* Stage */}
      <div ref={stageRef} className="relative flex-1 bg-gradient-to-br from-ink-50 to-canvas">
        <ViewerHost mode="ring">
          {({ ready, client, iframe }) => (
            <div className="relative h-full w-full">
              {iframe}

              {modelId && model && (
                <Stage
                  ready={ready}
                  client={client}
                  model={model}
                  onGroups={(g, m) => {
                    setGroups(g);
                    setMeta(m);
                  }}
                  onLoaded={() => setGlbReady(true)}
                />
              )}

              {/*
               * Loading overlay — a deterministic CSS fade, deliberately NOT
               * <AnimatePresence>.
               *
               * It used to be a motion.div with `initial` and `exit` but NO
               * `animate` target, so Framer never ran the exit: once the model
               * loaded the overlay stayed mounted at opacity 1 forever. Three
               * consequences, all on this route only:
               *   - "Loading model…" sat permanently over a fully loaded ring;
               *   - it kept pointer-events, so the model could not be orbited;
               *   - it fully covered the viewer iframe, which starved
               *     requestAnimationFrame and hung the frame-stepped video
               *     exporter indefinitely (exportTurntableVideo resolved in ~5s
               *     from /studio and never resolved from here).
               * Presence must therefore not depend on an animation completing.
               */}
              {(() => {
                const done = ready && glbReady;
                return (
                  <div
                    aria-hidden={done}
                    className="absolute inset-0 z-20 flex items-center justify-center bg-gradient-to-br from-ink-50 to-canvas"
                    /*
                     * Inline, not Tailwind utilities: `opacity-0` was applied but
                     * lost to a later stylesheet (styles/vault-ref.css loads last
                     * and wins), leaving the overlay fully opaque. `visibility`
                     * is switched with the transition-delay trick so the fade
                     * still plays, and so the finished state is genuinely
                     * non-painting — an opacity-0 layer that still occludes is
                     * what starved rAF and hung the video exporter here.
                     */
                    style={{
                      opacity: done ? 0 : 1,
                      visibility: done ? "hidden" : "visible",
                      pointerEvents: done ? "none" : "auto",
                      transition: done
                        ? "opacity 500ms ease, visibility 0s linear 500ms"
                        : "opacity 500ms ease",
                    }}
                  >
                    {model?.posterUrl ? (
                      <img src={model.posterUrl} alt="" className="max-h-[60%] max-w-[60%] object-contain opacity-50" />
                    ) : (
                      <Loader label="Loading model…" />
                    )}
                  </div>
                );
              })()}

              {/* Top-left: back + logo */}
              <div className="absolute left-4 top-4 z-30 flex items-center gap-2">
                <Logo size="sm" />
                <Link
                  to="/vault"
                  className="flex items-center gap-1.5 rounded-full border border-ink-200 bg-white/90 px-3 py-1.5 text-[12px] font-medium text-ink-700 shadow-sm backdrop-blur transition-colors hover:border-gold-300"
                >
                  <ArrowLeft size={14} /> Drive
                </Link>
              </div>

              {/* Floating control rail */}
              <div className="absolute right-4 top-1/2 z-30 flex -translate-y-1/2 flex-col gap-1.5 rounded-full border border-ink-100 bg-white/90 p-1.5 shadow-pop backdrop-blur">
                <RailButton title="Embed settings" onClick={() => setEmbedOpen(true)}>
                  <Settings2 size={16} />
                </RailButton>
                <RailButton title="Auto-rotate" active={rotate} disabled={!ready} onClick={() => {
                  const next = !rotate;
                  setRotate(next);
                  void client?.ring.setAutoRotate(next, 1);
                }}>
                  <Rotate3d size={16} />
                </RailButton>
                <RailButton title="Reset view" disabled={!ready} onClick={() => void client?.ring.resetView()}>
                  <RefreshCw size={16} />
                </RailButton>
                <RailButton title={fs ? "Exit fullscreen" : "Fullscreen"} onClick={toggleFullscreen}>
                  {fs ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </RailButton>
              </div>

              {/* Sidebar reopen handle (when collapsed) */}
              {!sidebar && (
                <button
                  onClick={() => setSidebar(true)}
                  className="absolute right-4 top-4 z-30 flex h-9 w-9 items-center justify-center rounded-full border border-ink-200 bg-white/90 text-ink-600 shadow-sm backdrop-blur hover:border-gold-300"
                  title="Show details"
                >
                  <PanelRightOpen size={16} />
                </button>
              )}

              {/* Configurator swatches */}
              {glbReady && groups.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="absolute inset-x-0 bottom-0 z-30 p-3 sm:p-4"
                >
                  <div className="glass-panel mx-auto flex max-w-2xl flex-col gap-3 rounded-2xl px-4 py-3.5 shadow-pop">
                    {meta.basePrice != null && (
                      <div className="flex items-baseline justify-between border-b border-ink-100 pb-2.5">
                        <span className="truncate text-[13px] font-semibold text-ink-900">{model?.name}</span>
                        <span className="text-[18px] font-semibold tracking-tight text-ink-900">{formatPrice(price, meta.currency)}</span>
                      </div>
                    )}
                    {groups.map((g) => (
                      <div key={g.id} className="flex items-center gap-3">
                        <span className="w-24 shrink-0 truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-500">{g.label}</span>
                        <div className="flex flex-wrap gap-1.5">
                          {g.options.map((o, i) => (
                            <button
                              key={o.id}
                              onClick={async () => {
                                const next = groups.map((x) => (x.id === g.id ? { ...x, activeIndex: i } : x));
                                setGroups(next);
                                if (o.metalColor) await client?.ring.setGroupMetalColor(g.groupId, o.metalColor);
                                if (o.gemPresetId) await client?.ring.applyGemPresetToGroup(g.groupId, o.gemPresetId);
                              }}
                              title={o.name}
                              className={`flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-3 text-[12px] font-medium transition-colors ${
                                g.activeIndex === i ? "border-gold-400 bg-gold-50 text-gold-700" : "border-ink-200 bg-white/80 text-ink-700 hover:border-gold-300"
                              }`}
                            >
                              <span className="h-5 w-5 rounded-full border border-black/10" style={{ background: o.swatch }} />
                              {o.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </ViewerHost>
      </div>

      {/* Info sidebar */}
      <AnimatePresence initial={false}>
        {sidebar && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 320, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="shrink-0 overflow-hidden border-l border-ink-100 bg-white"
          >
            <div className="flex h-full w-[320px] flex-col">
              <div className="flex items-start justify-between gap-3 border-b border-ink-100 px-5 py-4">
                <div className="min-w-0">
                  <h1 className="truncate font-headline text-[18px] text-ink-900">{model?.name ?? "Loading…"}</h1>
                  {model && <p className="mt-0.5 text-[11.5px] text-ink-400">{model.format.toUpperCase()}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setShare(true)} className="rounded-lg border border-ink-200 p-1.5 text-ink-600 hover:border-gold-300" title="Share">
                    <Share2 size={15} />
                  </button>
                  <button onClick={() => setSidebar(false)} className="rounded-lg p-1.5 text-ink-400 hover:bg-ink-50" title="Hide">
                    <PanelRightClose size={16} />
                  </button>
                </div>
              </div>

              <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
                <Detail label="Type" value={model?.format.toUpperCase() ?? "—"} />
                <Detail label="Uploader" value={user?.name ?? "—"} />
                <Detail label="Size" value={fmtSize(model?.glbSize || model?.size)} />
                <Detail label="Status" value={<StatusPill status={model?.status} />} />
                <div>
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">Tags</p>
                  {model?.tags.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {model.tags.map((t) => (
                        <span key={t} className="rounded-full bg-ink-50 px-2.5 py-1 text-[11.5px] text-ink-600">{t}</span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[12.5px] text-ink-400">No tags</p>
                  )}
                </div>
              </div>

              <div className="space-y-2 border-t border-ink-100 px-5 py-4">
                <button
                  onClick={() => model && navigate(`/studio/${model._id}`)}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-ink-900 px-3.5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-ink-800"
                >
                  <Pencil size={15} /> Open in Studio
                </button>
                <button
                  onClick={() => setEmbedOpen(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-ink-200 px-3.5 py-2.5 text-[13px] font-semibold text-ink-700 transition-colors hover:border-gold-300"
                >
                  <Settings2 size={15} /> Embed settings
                </button>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {share && model && <ShareModal model={model} onClose={() => setShare(false)} />}
      {embedOpen && model && <EmbedSettings model={model} onClose={() => setEmbedOpen(false)} />}
    </div>
  );
}

function Stage({
  ready,
  client,
  model,
  onGroups,
  onLoaded,
}: {
  ready: boolean;
  client: ViewerClient | null;
  model: VaultModel;
  onGroups: (groups: LiveGroup[], meta: { basePrice?: number; currency: string }) => void;
  onLoaded: () => void;
}) {
  const loaded = useRef(false);
  useEffect(() => {
    if (!ready || !client || loaded.current) return;
    loaded.current = true;
    (async () => {
      try {
        const { url } = await getModelGlbUrl(model._id);
        const native = ["3dm", "obj", "fbx", "stl"].includes(model.format);
        const vjson = (model.config as { vjson?: string } | undefined)?.vjson;
        // v65: a saved scene wins over one inside the file; without one, the file's own look shows.
        await client.ring.addModelUrl(
          url,
          withEmbeddedScene(native ? { autoCenter: true, autoScale: true } : undefined, capsFor(client.contractVersion).sceneFileV65, !!vjson),
        );

        if (vjson) {
          try {
            await client.ring.loadVjsonFile(new File([vjson], "config.vjson", { type: "application/json" }));
          } catch {
            /* best-effort */
          }
        }

        const viewerGroups = await client.ring.listGroups();
        const saved = (model.config as { configurator?: ConfiguratorDef } | undefined)?.configurator;
        const def: ConfiguratorDef = effectiveConfigurator(saved, viewerGroups);
        // A group the designer emptied is persisted but has nothing to show.
        const live: LiveGroup[] = matchGroupTargets(def.groups, viewerGroups)
          .filter(({ group }) => group.options.length > 0)
          .map(({ group, target }) => ({ ...group, groupId: target.id, activeIndex: defaultIndexOf(group) }));
        // Wear the option each group opens on, or the highlighted swatch and the
        // quoted price describe a model that isn't on screen. Authored defs only
        // — the auto-built fallback's index-0 default is a guess, and applying it
        // would restyle every model nobody has configured yet.
        if (saved?.groups?.length) {
          for (const g of live) {
            const opt = g.options[g.activeIndex];
            if (opt?.metalColor) await client.ring.setGroupMetalColor(g.groupId, opt.metalColor);
            if (opt?.gemPresetId) await client.ring.applyGemPresetToGroup(g.groupId, opt.gemPresetId);
          }
        }
        onGroups(live, { basePrice: def.basePrice, currency: def.currency ?? "USD" });
      } catch {
        /* leave bare viewer */
      } finally {
        onLoaded();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, client, model._id]);
  return null;
}

function RailButton({
  children,
  title,
  active,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors disabled:opacity-40 ${
        active ? "bg-gold-500 text-white" : "text-ink-600 hover:bg-ink-100"
      }`}
    >
      {children}
    </button>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">{label}</p>
      <div className="text-[13px] text-ink-800">{value}</div>
    </div>
  );
}

function StatusPill({ status }: { status?: VaultModel["status"] }) {
  if (status === "ready") return <Pill tone="emerald">Ready</Pill>;
  if (status === "error") return <Pill tone="dark">Failed</Pill>;
  return <Pill tone="gold">Processing</Pill>;
}
